import { useContext } from 'react';
import { Context } from '../Context';
import { completeTargetURL } from '../utils/trackingUtils';
import type { NumbersItem } from '../../types';
// import { ModuleType } from '../../types';

export type LinkElement = 'link' | 'cta';
export type ButtonLabels = Array<{ number: number; linkElement: LinkElement; label: string }>;
export interface Tracking {
  sendClickEvent(
    linkLabel: string,
    linkUrl: string,
    eventName: string,
    linkElement: 'text link' | 'button',
    layer: 'layer' | 'other',
    index?: number,
    ctaList?: NumbersItem[],
  ): void;
  sendReadyEvent(): void;
  registerImpressionTracking(heading?: string, buttonLabels?: ButtonLabels | null): void;
  // moduleType?: ModuleType;
}

export function useTracking(
  attributes?: {
    version?: string;
    implementer?: number;
    [key: string]: unknown;
  },
  ref?: React.MutableRefObject<null>,
  // moduleType?: ModuleType,
): Tracking {
  const { trackingService, logger } = useContext(Context);
  const featureAppName = (typeof __FEATURE_APP_NAME__ === 'string' && __FEATURE_APP_NAME__) || '';
  // const moduleName = moduleType === ModuleType.NUMBERS ? 'numbers' : 'editorial-columns';
  // editorial-columns numbers
  const componentNameWithDashes = featureAppName.replace('fa-', '');
  const componentNameWithSpaces = componentNameWithDashes.replace(/-/g, ' ');

  const defaultComponentAdditions = {
    componentInfo: {
      // not accepted by tracking lib
      // componentID: 'bla',
    },
    attributes: {
      version: __FEATURE_APP_VERSION__,
      ...attributes,
    },
  };

  return {
    sendClickEvent: (
      linkLabel: string,
      linkUrl: string,
      eventName: 'text link' | 'cta' | 'cta - layer open' | 'text link - layer open',
      linkElement: 'text link' | 'button',
      layer: 'layer' | 'other',
      index?: number,
    ) => {
      if (trackingService) {
        const targetURL = completeTargetURL(linkUrl);
        const fallback = targetURL ? 'internal_link' : '';
        const eventAction =
          layer === 'layer' || targetURL.startsWith('#')
            ? 'content'
            : (trackingService.evaluateLinkType(targetURL) ?? fallback);
        if (!eventAction) {
          // do not track click event if link type could not be evaluated
          return;
        }
        const event = {
          event: {
            attributes: {
              componentName: componentNameWithDashes,
              elementName: linkElement,
              label: linkLabel,
              clickID: '',
              pos: '',
              targetURL,
              value: '',
              relatedElements: [
                {
                  elementName:
                    eventName === 'text link' || eventName === 'text link - layer open'
                      ? 'link'
                      : 'cta',
                  elementValue: `${index}-${linkLabel}`,
                },
              ],
            },
            eventInfo: {
              eventAction,
              eventName: `${componentNameWithSpaces} - click on ${eventName}`,
            },
          },
          componentUpdate: defaultComponentAdditions,
        };
        logger?.info(event);
        trackingService.track(event);
      }
    },
    sendReadyEvent: () => {
      if (trackingService) {
        const readyEvent = {
          event: {
            attributes: {
              componentName: componentNameWithDashes,
              elementName: '',
              label: '',
              clickID: '',
              pos: '',
              targetURL: '',
              value: '',
            },
            eventInfo: {
              eventAction: 'feature_app_ready',
              eventName: `${componentNameWithSpaces} - feature app ready`,
            },
          },
          componentUpdate: defaultComponentAdditions,
        };
        logger?.info(readyEvent);
        trackingService.track(readyEvent);
      }
    },
    registerImpressionTracking: (
      heading?: string,
      buttonLabels?: Array<{ number: number; linkElement: 'link' | 'cta'; label: string }> | null,
    ) => {
      if (trackingService && ref?.current) {
        trackingService.registerImpressionTracking(ref.current, () => {
          return {
            event: {
              attributes: {
                componentName: componentNameWithDashes,
                elementName: '',
                label: '',
                clickID: '',
                value: heading || '',
                pos: '',
                targetURL: '',
                relatedElements: buttonLabels
                  ? buttonLabels.map((el) => ({
                      elementName: el.linkElement,
                      elementValue: `${el.number}-${el.label}`,
                    }))
                  : [],
              },
              eventInfo: {
                eventAction: 'impression',
                eventName: `${componentNameWithSpaces} - impression`,
              },
            },
            componentUpdate: defaultComponentAdditions,
          };
        });
      }
    },
  };
}
